import { useMemo } from "react";
import { ArrowRight, Info, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { ErpPropertyOption } from "@forgepoint/shared";
import {
  areStringRecordsEqual,
  autoMapSelectOptionValues,
} from "./customFieldMappingHelpers";
import { SearchableErpOptionSelect } from "./SearchableErpOptionSelect";

export type { ErpPropertyOption };

/** Sentinel value used for "Not mapped" in the select. */
const NOT_MAPPED_VALUE = "__not_mapped__";

interface SelectOptionMappingRowsProps {
  /** FP select field option values */
  fpOptions: string[];
  /** Display labels keyed by FP option value; rows show the value when absent. */
  fpOptionLabels?: Record<string, string>;
  /** Available ERP enum options for the selected ERP property */
  erpOptions: ErpPropertyOption[];
  /** Allows free-text ERP values when the provider cannot enumerate them. */
  allowManualValues?: boolean;
  /** Whether an entirely empty option map sends the source value unchanged. */
  passThroughWhenEmpty?: boolean;
  /** Current FP option -> ERP value mapping */
  optionMapping: Record<string, string>;
  /** Called when option mapping changes */
  onOptionMappingChange: (optionMapping: Record<string, string>) => void;
  /** Heading above the rows. */
  title?: string;
  /** Renders a searchable combobox instead of a plain select, for long option lists. */
  searchable?: boolean;
  /** Shows the auto-map by name button. */
  showAutoMap?: boolean;
  /** Footnote shown while some options are unmapped. */
  unmappedHint?: string;
}

/**
 * Renders a row per FP option with a dropdown of ERP values, e.g. below a
 * select field mapped to an ERP enumeration property.
 * @param props - SelectOptionMappingRowsProps
 * @returns The option mapping rows element
 */
export function SelectOptionMappingRows({
  fpOptions,
  fpOptionLabels = {},
  erpOptions,
  allowManualValues = false,
  passThroughWhenEmpty = false,
  optionMapping,
  onOptionMappingChange,
  title = "Map option values",
  searchable = false,
  showAutoMap = true,
  unmappedHint = "Unmapped options will be skipped during sync.",
}: SelectOptionMappingRowsProps) {
  const mappedCount = fpOptions.filter((opt) => optionMapping[opt]).length;
  const autoMappedOptionMapping = useMemo(
    () =>
      autoMapSelectOptionValues({
        fpOptions,
        erpOptions,
        optionMapping,
      }),
    [erpOptions, fpOptions, optionMapping],
  );
  const canAutoMap = !areStringRecordsEqual(
    optionMapping,
    autoMappedOptionMapping,
  );

  /**
   * Maps an FP option to an ERP value, or unmaps it when the value is empty.
   * @param fpOption - The FP option being mapped
   * @param erpValue - The ERP value, or empty to unmap
   */
  const setOptionValue = (fpOption: string, erpValue: string) => {
    if (!erpValue) {
      const { [fpOption]: _, ...rest } = optionMapping;
      onOptionMappingChange(rest);
      return;
    }
    onOptionMappingChange({ ...optionMapping, [fpOption]: erpValue });
  };

  return (
    <div className="ml-6 grid gap-2.5 border-l-2 border-muted pl-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {title}
        </span>
        <div className="flex items-center gap-2">
          {showAutoMap && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onOptionMappingChange(autoMappedOptionMapping)}
              disabled={!canAutoMap}
            >
              <Sparkles className="h-3 w-3" />
              Auto-map
            </Button>
          )}
          <span
            className={cn(
              "text-[11px] tabular-nums transition-colors",
              mappedCount === fpOptions.length && mappedCount > 0
                ? "text-emerald-600"
                : "text-muted-foreground",
            )}
          >
            {mappedCount}/{fpOptions.length}
          </span>
        </div>
      </div>
      {fpOptions.map((fpOption) => {
        const isMapped = !!optionMapping[fpOption];
        const ownValue = optionMapping[fpOption] ?? "";

        // Collect ERP values already claimed by other FP options
        const usedByOthers = new Set<string>();
        for (const [otherFpOption, erpValue] of Object.entries(optionMapping)) {
          if (otherFpOption !== fpOption && erpValue) {
            usedByOthers.add(erpValue);
          }
        }
        const availableErpOptions = erpOptions.filter(
          (opt) => opt.value === ownValue || !usedByOthers.has(opt.value),
        );

        /**
         * Picks the value control for this row: manual input, searchable combobox, or plain select.
         * @returns The value control element
         */
        const renderValueControl = () => {
          if (allowManualValues && erpOptions.length === 0) {
            return (
              <Input
                value={ownValue}
                placeholder="ERP value (optional)"
                className="h-8 text-sm"
                onChange={(event) =>
                  setOptionValue(fpOption, event.target.value)
                }
              />
            );
          }
          if (searchable) {
            return (
              <SearchableErpOptionSelect
                options={availableErpOptions}
                value={ownValue}
                onValueChange={(val) => setOptionValue(fpOption, val)}
              />
            );
          }
          return (
            <Select
              value={optionMapping[fpOption] ?? NOT_MAPPED_VALUE}
              onValueChange={(val) => {
                if (val === NOT_MAPPED_VALUE) {
                  setOptionValue(fpOption, "");
                  return;
                }
                setOptionValue(fpOption, val);
              }}
            >
              <SelectTrigger
                className={cn(
                  "h-8 text-sm",
                  !isMapped && "text-muted-foreground",
                )}
              >
                <SelectValue placeholder="Select value..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOT_MAPPED_VALUE}>Not mapped</SelectItem>
                {availableErpOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        };

        return (
          <div
            key={fpOption}
            className="grid grid-cols-[1fr_auto_1.5fr] items-center gap-2"
          >
            <span
              className={cn(
                "flex items-center gap-1.5 text-sm transition-colors",
                !isMapped && "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "inline-block h-1.5 w-1.5 shrink-0 rounded-full transition-colors",
                  isMapped ? "bg-emerald-500" : "bg-border",
                )}
              />
              <span className="min-w-0 break-words">
                {fpOptionLabels[fpOption] ?? fpOption}
              </span>
            </span>
            <ArrowRight
              className={cn(
                "h-3 w-3 shrink-0 transition-colors",
                isMapped ? "text-muted-foreground" : "text-muted-foreground/30",
              )}
            />
            {renderValueControl()}
          </div>
        );
      })}
      {mappedCount < fpOptions.length && (
        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          {mappedCount === 0 && passThroughWhenEmpty
            ? "Options pass through unchanged until value mappings are added."
            : unmappedHint}
        </p>
      )}
    </div>
  );
}
